import React, { memo, useMemo } from 'react';
import cn from 'classnames';

import {
	checkIsEmptyChart,
	generateListOfCompareMessages,
	generateListOfCorrelateMessages,
} from '@utils/kpi-utils/chart';

import { IChartCardProps } from './ChartCard.types';

import Chart from '../../../common/Chart';
import ChartControlPanel from '../../../common/ChartControlPanel';
import ThresholdText from '../../../common/ThresholdText';
import ChartCardHeader from './ChartCardHeader';

import './ChartCard.scss';

const ChartCard = ({
	addChartToCompare,
	addChartToCorrelate,
	chart,
	compareCharts,
	compareSettings,
	correlateCharts,
	correlateSettings,
	height,
	isMap = false,
	onDeleteChart,
	openFileTypeSelectionModal,
	selectedIds,
	setAllCharts,
	setChangedSizeCharts,
	setFullChart,
	setSelectedIds,
	setVisibleThresholdIds,
	visibleThresholdIds,
	panTo,
}: IChartCardProps) => {
	const thresholds = chart.thresholds;

	const isThreshold = visibleThresholdIds.includes(chart.chartId);
	const isEmptyChart = checkIsEmptyChart(chart);
	const isDisabledThreshold = isEmptyChart || !thresholds.length;
	const isCheckedCompare = compareCharts.map(compareChart => compareChart.chartId).includes(chart.chartId);
	const isCheckedCorrelate = correlateCharts.map(correlateChart => correlateChart.chartId).includes(chart.chartId);

	const listOfTooltipCompareMessage = useMemo(() => generateListOfCompareMessages({
		compareCharts,
		chart,
		compareSettings,
		isEmptyChart,
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}), [compareSettings]);

	const listOfTooltipCorrelateMessage = useMemo(() => generateListOfCorrelateMessages({
		correlateCharts,
		chart,
		correlateSettings,
		isEmptyChart,
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}), [correlateSettings]);

	const toggleCollapsibleBlock = () => {
		setAllCharts(prevState => prevState.map(chatItem => chatItem.chartId === chart.chartId
			? ({
				...chatItem,
				isOpen: !chatItem.isOpen,
			})
			: chatItem));

		setChangedSizeCharts({ ids: [chart.chartId], isOpen: !chart.isOpen });
	};

	const toggleThreshold = () => isThreshold
		? setVisibleThresholdIds(prevState => prevState.filter(id => id !== chart.chartId))
		: setVisibleThresholdIds(prevState => ([...prevState, chart.chartId]));

	const openFullCard = () => !isEmptyChart && setFullChart(chart);

	const downloadChart = () => openFileTypeSelectionModal({ chart });

	const chartCardContainerClasses = cn('f5gChartCardContainer', { 'f5gChartCardContainerEmpty': isEmptyChart && chart.isOpen });

	const chartCardWrapperClasses = cn('f5gChartCardWrapper', {
			'f5gChartCardWrapperMinHeightFilledOnMap': isMap,
			'f5gChartCardWrapperEmpty': isEmptyChart && chart.isOpen,
		},
	);

	return (
		<div className={chartCardContainerClasses}>
			<div className={chartCardWrapperClasses}>
				<ChartCardHeader
					addChartToCompare={addChartToCompare}
					addChartToCorrelate={addChartToCorrelate}
					chart={chart}
					isCheckedCompare={isCheckedCompare}
					isCheckedCorrelate={isCheckedCorrelate}
					isOpenChart={!!chart.isOpen}
					listOfTooltipCompareMessage={listOfTooltipCompareMessage}
					listOfTooltipCorrelateMessage={listOfTooltipCorrelateMessage}
					onDeleteChart={onDeleteChart}
					panTo={panTo}
					selectedIds={selectedIds}
					setSelectedIds={setSelectedIds}
					toggleCollapsibleBlock={toggleCollapsibleBlock}
				/>
				<div className={cn('f5gChartCardCollapsibleBody', { 'f5gChartCardCollapsibleBodyClose': !chart.isOpen })}>
					<div>
						{isEmptyChart
							? <div
								className='f5gChartCardEmpty'
								style={{ height }}
							>
								No data available
							</div>
							: <Chart
								charts={[chart]}
								height={height}
								isAnimation={false}
								isLegend={false}
								isMaintainAspectRatio={false}
								isThreshold={isThreshold}
							/>
						}
					</div>
					<ThresholdText
						chart={chart}
						isVisible={isThreshold}
					/>
					<ChartControlPanel
						chart={chart}
						downloadChart={downloadChart}
						isCheckedThreshold={isThreshold}
						isDisabledExpand={isEmptyChart}
						isDisabledExport={isEmptyChart}
						isDisabledThreshold={isDisabledThreshold}
						isExpandable
						openFullCard={openFullCard}
						toggleThreshold={toggleThreshold}
					/>
				</div>
			</div>
		</div>
	);
};

export default memo(ChartCard);
